import React from 'react';
import { Phone, Settings, Bell, CheckCircle2, Gift, Headphones, MessageCircle } from 'lucide-react';

// Message affiché au client dont l'offre (essai ou abonnement) est active mais dont l'agent IA n'est pas encore activé :
// l'équipe TeckiA l'appelle pour comprendre son activité, configure l'agent, puis le prévient dans l'app.
// variant 'full' : remplace l'ancien formulaire (WhatsApp) · variant 'banner' : bandeau compact au-dessus de la connexion de la Page (Facebook).

const CSS = `
.tc-scene { perspective: 1200px; }
.tc-card { position:relative; border-radius:30px; padding:30px 24px 26px; overflow:hidden; text-align:center; transform-style:preserve-3d; transform:rotateX(2.5deg);
  background:linear-gradient(155deg,rgba(255,255,255,.12),rgba(255,255,255,.025)); border:1px solid rgba(255,255,255,.2);
  box-shadow:0 44px 90px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.3); transition:transform .4s; }
.tc-card:hover { transform:rotateX(0) translateY(-3px); }
.tc-glow { position:absolute; width:340px; height:340px; border-radius:50%; filter:blur(70px); opacity:.33; pointer-events:none; }
.tc-stage { position:relative; height:150px; display:flex; align-items:center; justify-content:center; perspective:700px; margin-bottom:8px; }
.tc-floor { position:absolute; left:8%; right:8%; bottom:-8px; height:70px; transform:rotateX(68deg); transform-origin:50% 100%; opacity:.5; border-radius:50%;
  background:radial-gradient(ellipse at center,var(--a) 0,transparent 68%); }
.tc-tile { position:relative; width:96px; height:96px; border-radius:28px; display:flex; align-items:center; justify-content:center; color:#fff; transform-style:preserve-3d;
  background:linear-gradient(145deg,var(--a),var(--b)); box-shadow:0 28px 50px rgba(0,0,0,.55), inset 0 2px 0 rgba(255,255,255,.4), inset 0 -6px 0 rgba(0,0,0,.25); animation:tcSpin 8s ease-in-out infinite; }
@keyframes tcSpin { 0%,100%{transform:rotateY(-22deg) rotateX(7deg) translateY(0)} 50%{transform:rotateY(22deg) rotateX(-3deg) translateY(-10px)} }
.tc-gear { position:absolute; right:-16px; top:-16px; width:46px; height:46px; border-radius:16px; display:flex; align-items:center; justify-content:center; color:#fff;
  background:linear-gradient(145deg,#f59e0b,#ef4444); box-shadow:0 12px 24px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.4); transform:translateZ(36px); }
.tc-gear svg { animation:tcRot 6s linear infinite; }
@keyframes tcRot { to { transform:rotate(360deg); } }
.tc-chip { position:absolute; display:inline-flex; align-items:center; gap:6px; padding:7px 12px; border-radius:13px; font-weight:700; font-size:12px; white-space:nowrap; color:#fff;
  background:linear-gradient(145deg,rgba(255,255,255,.17),rgba(255,255,255,.05)); border:1px solid rgba(255,255,255,.22); backdrop-filter:blur(10px); box-shadow:0 12px 26px rgba(0,0,0,.4); animation:tcFloat 6s ease-in-out infinite; }
@keyframes tcFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
.tc-steps { display:flex; align-items:flex-start; justify-content:center; gap:0; margin:22px auto 4px; max-width:520px; }
.tc-step { flex:1; display:flex; flex-direction:column; align-items:center; gap:7px; position:relative; min-width:0; }
.tc-step::before { content:''; position:absolute; top:17px; left:-50%; width:100%; height:3px; border-radius:2px; background:rgba(255,255,255,.14); z-index:0; }
.tc-step:first-child::before { display:none; }
.tc-step.done::before, .tc-step.now::before { background:linear-gradient(90deg,#4ade80,var(--a)); }
.tc-dot { position:relative; z-index:1; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:rgba(255,255,255,.55);
  background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.18); }
.tc-step.done .tc-dot { background:linear-gradient(145deg,#86efac,#22c55e); color:#052e1a; border-color:transparent; box-shadow:0 6px 0 #15803d, 0 12px 20px rgba(34,197,94,.3); }
.tc-step.now .tc-dot { background:linear-gradient(145deg,var(--a),var(--b)); color:#fff; border-color:transparent; box-shadow:0 6px 0 rgba(0,0,0,.35), 0 0 0 6px rgba(255,255,255,.08); animation:tcPulse 2s ease-in-out infinite; }
@keyframes tcPulse { 0%,100%{box-shadow:0 6px 0 rgba(0,0,0,.35), 0 0 0 4px rgba(255,255,255,.08)} 50%{box-shadow:0 6px 0 rgba(0,0,0,.35), 0 0 0 11px rgba(255,255,255,0)} }
.tc-step span { font-size:11.5px; line-height:1.25; color:rgba(226,232,255,.65); max-width:92px; }
.tc-step.now span, .tc-step.done span { color:#fff; font-weight:700; }
.tc-banner { display:flex; align-items:center; gap:14px; text-align:left; border-radius:22px; padding:16px 18px; border:1px solid rgba(255,255,255,.18); position:relative; overflow:hidden;
  background:linear-gradient(135deg,rgba(255,255,255,.1),rgba(255,255,255,.025)); box-shadow:0 22px 46px rgba(0,0,0,.4); transform:perspective(900px) rotateX(1.5deg); }
.tc-mini { flex:none; width:52px; height:52px; border-radius:17px; display:flex; align-items:center; justify-content:center; color:#fff; position:relative;
  background:linear-gradient(145deg,var(--a),var(--b)); box-shadow:0 10px 20px rgba(0,0,0,.4), inset 0 2px 0 rgba(255,255,255,.4); animation:tcFloat 5s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .tc-tile, .tc-gear svg, .tc-chip, .tc-mini, .tc-step.now .tc-dot, .tc-card { animation:none; transition:none; } }
@media (max-width:520px) { .tc-chip { display:none; } }
`;

const THEMES = {
  facebook: { a: '#2563eb', b: '#6366f1', label: 'Messenger' },
  whatsapp: { a: '#16a34a', b: '#059669', label: 'WhatsApp' },
};

export default function TeamConfiguring({ channel = 'facebook', phone = '', daysLeft = null, kind = 'trial', variant = 'full', onSupport }) {
  const t = THEMES[channel] || THEMES.facebook;
  const vars = { '--a': t.a, '--b': t.b };
  const who = channel === 'whatsapp' ? 'votre agent WhatsApp' : 'votre agent Messenger';
  const callTxt = phone
    ? <>Un membre de l'équipe va vous appeler au <b className="text-white whitespace-nowrap">{phone}</b> pour discuter de votre activité et recueillir les informations dont l'agent a besoin.</>
    : <>Un membre de l'équipe va vous appeler pour discuter de votre activité et recueillir les informations dont l'agent a besoin.</>;

  if (variant === 'banner') {
    return (
      <div className="mb-6" style={vars}>
        <style>{CSS}</style>
        <div className="tc-banner">
          <div className="tc-mini"><Headphones className="w-6 h-6" /></div>
          <div className="min-w-0">
            <div className="text-white font-bold">L'équipe TeckiA configure {who}</div>
            <p className="text-sm text-blue-100/85 mt-0.5">{callTxt} Vous recevrez une <b className="text-white">notification</b> dès qu'il sera activé de votre côté.</p>
            {onSupport && <button onClick={onSupport} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold text-white transition"><MessageCircle className="w-3.5 h-3.5" /> Écrire à l'équipe</button>}
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { icon: Gift, text: kind === 'trial' ? 'Essai démarré' : 'Offre activée', state: 'done' },
    { icon: Phone, text: "Appel de l'équipe", state: 'now' },
    { icon: Settings, text: 'Configuration', state: '' },
    { icon: Bell, text: 'Agent activé', state: '' },
  ];

  return (
    <div className="tc-scene mb-8" style={vars}>
      <style>{CSS}</style>
      <div className="tc-card">
        <div className="tc-glow" style={{ background: t.a, right: -120, top: -140 }} />
        <div className="tc-glow" style={{ background: t.b, left: -140, bottom: -180, opacity: 0.22 }} />
        <div className="relative">
          <div className="tc-stage">
            <div className="tc-floor" />
            <div className="tc-chip" style={{ left: '6%', top: '14%' }}>📞 Appel de bienvenue</div>
            <div className="tc-chip" style={{ right: '5%', top: '20%', animationDelay: '-2s' }}>🔔 Alerte d'activation</div>
            <div className="tc-chip" style={{ left: '11%', bottom: '10%', animationDelay: '-4s' }}>🤝 Sur mesure</div>
            <div className="tc-tile">
              <Headphones className="w-11 h-11" />
              <div className="tc-gear"><Settings className="w-6 h-6" /></div>
            </div>
          </div>

          <h2 className="text-xl md:text-2xl font-extrabold text-white">L'équipe TeckiA configure {who}</h2>
          <p className="text-sm md:text-[15px] text-blue-100/85 mt-2 max-w-xl mx-auto leading-relaxed">{callTxt}</p>
          <p className="text-sm md:text-[15px] text-blue-100/85 mt-2 max-w-xl mx-auto leading-relaxed">
            Vous n'avez rien d'autre à faire : vous recevrez une <b className="text-white">notification</b> dans l'application dès que {who} sera activé de votre côté.
          </p>

          <div className="tc-steps">
            {steps.map(s => (
              <div key={s.text} className={`tc-step ${s.state}`}>
                <div className="tc-dot">{s.state === 'done' ? <CheckCircle2 className="w-5 h-5" /> : React.createElement(s.icon, { className: 'w-4 h-4' })}</div>
                <span>{s.text}</span>
              </div>
            ))}
          </div>

          {onSupport && (
            <div className="mt-5">
              <button onClick={onSupport} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-bold text-white bg-gradient-to-br from-violet-500 to-indigo-600 transition active:translate-y-0.5" style={{ boxShadow: '0 5px 0 #3730a3, 0 14px 24px rgba(79,70,229,.35)' }}>
                <MessageCircle className="w-4 h-4" /> Écrire à l'équipe
              </button>
            </div>
          )}

          {daysLeft != null && (
            <p className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold text-emerald-100 bg-emerald-500/15 border border-emerald-400/30">
              <Gift className="w-3.5 h-3.5" /> {kind === 'trial' ? `Essai gratuit : encore ${daysLeft} jour${daysLeft > 1 ? 's' : ''}` : `Offre active : encore ${daysLeft} jour${daysLeft > 1 ? 's' : ''}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
